import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Game info mapping
const GAME_INFO = {
  'get-to-know-you': { name: 'Get to Know You', emoji: '💬', color: 'from-blue-500 to-cyan-500', description: 'Fun questions to learn more about each other' },
  'love-psychology-quiz': { name: 'Love & Psychology Quiz', emoji: '💕', color: 'from-pink-500 to-rose-500', description: 'Discover your love language and relationship style' },
  'compatibility-challenge': { name: 'Compatibility Challenge', emoji: '🧠', color: 'from-purple-500 to-indigo-500', description: 'Test how compatible you are!' },
  'ping-pong': { name: 'Ping Pong Game', emoji: '🏓', color: 'from-green-500 to-emerald-500', description: 'Play and compete for 100 points!' }
};

const GameInvitePage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null); // Track which match is being sent to
  const [currentUser, setCurrentUser] = useState(null);

  const gameInfo = GAME_INFO[gameId];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, matchesRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/matches`, { withCredentials: true })
      ]);
      
      setCurrentUser(userRes.data);
      setMatches(matchesRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const sendGameInvite = async (match) => {
    setSending(match.match_id);
    
    try {
      const inviteMessage = `[GAME_INVITE:${JSON.stringify({
        gameId,
        gameName: gameInfo.name,
        emoji: gameInfo.emoji,
        inviterName: currentUser?.name || 'Someone'
      })}]`;
      
      await axios.post(
        `${API}/matches/${match.match_id}/messages`,
        { text: inviteMessage },
        { withCredentials: true }
      );
      
      toast.success(`${gameInfo.emoji} Invite sent to ${match.user?.name || match.matched_user?.name}!`);
      
      // Navigate to chat to see the sent widget
      navigate(`/chat/${match.match_id}`);
    } catch (error) {
      console.error('Failed to send game invite:', error);
      toast.error('Failed to send invite');
      setSending(null);
    }
  };

  if (!gameInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Game not found</p>
          <Button onClick={() => navigate('/games')} className="mt-4">
            Back to Games
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center space-x-3">
          <Button
            onClick={() => navigate('/games')}
            variant="ghost"
            size="icon"
            className="hover:bg-pink-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 text-transparent bg-clip-text">
              {gameInfo.emoji} {gameInfo.name}
            </h1>
            <p className="text-sm text-gray-600">Select a match to invite</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Game Preview Card */}
        <div className={`bg-gradient-to-r ${gameInfo.color} rounded-2xl p-6 text-white shadow-lg`}>
          <div className="flex items-center space-x-4">
            <div className="text-5xl">{gameInfo.emoji}</div>
            <div>
              <h2 className="text-2xl font-bold">{gameInfo.name}</h2>
              <p className="text-white/80">{gameInfo.description}</p>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-white/80 rounded-xl p-4 border border-purple-100">
          <p className="text-gray-700 text-sm">
            👇 Tap on a match below to send them a game invite. They will receive a widget in chat to start playing!
          </p>
        </div>

        {/* Matches List */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800">Your Matches</h3>
          
          {matches.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center">
              <p className="text-gray-500 mb-4">No matches yet</p>
              <Button onClick={() => navigate('/discover')} variant="outline">
                Find Matches
              </Button>
            </div>
          ) : (
            matches.map((match) => {
              const matchedUser = match.user || match.matched_user;
              const isSending = sending === match.match_id;
              
              return (
                <div
                  key={match.match_id}
                  onClick={() => !isSending && sendGameInvite(match)}
                  className={`bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-purple-300 ${
                    isSending ? 'opacity-70 pointer-events-none' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <Avatar className="w-14 h-14 border-2 border-purple-200">
                        <AvatarImage src={matchedUser?.photos?.[0]} alt={matchedUser?.name} />
                        <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-400 text-white text-lg">
                          {matchedUser?.name?.[0] || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="font-semibold text-gray-800">{matchedUser?.name || 'Unknown'}</h4>
                        <p className="text-sm text-gray-500">Tap to send invite</p>
                      </div>
                    </div>
                    <div className={`p-3 rounded-full bg-gradient-to-r ${gameInfo.color}`}>
                      {isSending ? (
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                      ) : (
                        <Send className="w-5 h-5 text-white" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default GameInvitePage;
